import * as React from 'react'
import { useMemo, useState } from 'react'
import { Combobox } from 'components/UI/combobox/combobox'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { useListProductsCatalog } from 'services/products'
import { TriangleAlert, ChevronDown } from 'lucide-react'

export interface ProductSelectorProps {
  value?: number | null
  onChange: (productId: number | null) => void
  placeholder?: string
  disabled?: boolean
}

/**
 * Reusable product selector component for forms.
 * Displays current product name with warning if unmatched.
 * Opens combobox dropdown on click.
 */
export function ProductSelector({
  value,
  onChange,
  placeholder = 'Select a product',
  disabled = false,
}: ProductSelectorProps) {
  const [open, setOpen] = useState(false)

  // Fetch products for the dropdown
  const { data: productsData, isLoading } = useListProductsCatalog({
    page: 1,
    page_size: 1000,
    sort: 'name',
  })

  // Convert products to combobox options
  const productOptions = useMemo(() => {
    if (!productsData?.results) return []
    return productsData.results.map((product: any) => ({
      label: product.name,
      value: product.id,
    }))
  }, [productsData])

  // Find selected product name
  const selectedProduct = useMemo(() => {
    if (!value) return null
    const product = productOptions.find((opt) => opt.value === value)
    return product || null
  }, [value, productOptions])

  const [selected, setSelected] = React.useState<string | number | undefined>(
    value ?? undefined
  )

  // Sync selected state with value prop
  React.useEffect(() => {
    setSelected(value ?? undefined)
  }, [value])

  const handleSelect: React.Dispatch<
    React.SetStateAction<string | number | undefined>
  > = (selectedValue) => {
    // Handle function case (though unlikely for this component)
    const actualValue =
      typeof selectedValue === 'function'
        ? selectedValue(selected)
        : selectedValue

    setSelected(actualValue)

    if (actualValue === undefined || actualValue === '') {
      onChange(null)
    } else {
      onChange(Number(actualValue))
    }

    setOpen(false)
  }

  // Determine button content
  const buttonContent = useMemo(() => {
    // If loading and we have a value, show loading
    if (isLoading && value) {
      return <span className="text-gray-500">Loading product...</span>
    }

    // If we found the product, show its name
    if (selectedProduct) {
      return <span className="text-gray-900">{selectedProduct.label}</span>
    }

    // If we have a value but no product found (and not loading), still show No Product
    // This handles the case where the product might be deleted
    return (
      <div className="flex items-center gap-2 text-yellow-600">
        <TriangleAlert size={16} className="flex-shrink-0" />
        <span className="text-gray-400">No Product</span>
      </div>
    )
  }, [isLoading, value, selectedProduct])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {buttonContent}
          <ChevronDown size={16} className="ml-2 flex-shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Combobox
          options={productOptions}
          selected={selected}
          onSelect={handleSelect}
          searchPlaceholder="Search products..."
          placeholder={placeholder}
          loading={isLoading}
          notFoundPlaceholder="No products found"
          buttonLess
        />
      </PopoverContent>
    </Popover>
  )
}
